import type {
  CatalogProductsAPIQueryParams,
  ColorAPIResponse,
  FiltersConfig,
  FilterValuesState,
  ListedProduct,
  MapFilterValue,
  ProductAPIResponse,
  QueryProductsAPIResponse,
  QueryProductsAPIResponseFilter,
  SortingVariant
} from '~/types/shop'
import {
  CatalogProductsAPIOrderBy,
  Filter,
  FilterConfigLayout,
  LISTED_PRODUCT_OMITTED_FIELDS,
  UnitVisible
} from '~/types/shop'
import { sortSwatches } from '~/utils/format/color-swatches'
import type { CategoryItem } from '~/types/root'

const listedProductOmittedFields: ReadonlySet<string> = new Set(LISTED_PRODUCT_OMITTED_FIELDS)

/**
 * A product as a list keeps it: without the prose fields no list shows (see ListedProduct).
 */
export const toListedProduct = (product: ProductAPIResponse): ListedProduct =>
  Object.fromEntries(Object.entries(product).filter(([key]) => !listedProductOmittedFields.has(key))) as ListedProduct

/**
 * Configuration object for filter
 *
 * Note: with `toggle: true` the gating feature key defaults to `filter-${key}`.
 * Set `toggleKey` to override (e.g. one shared key for a coordinated set).
 * Set `toggleInverse: true` to hide the filter when the key IS in the enabled-features set.
 */
export const filterConfig: FiltersConfig = {
  inventoryStatuses: { order: 0 },
  discountPercents: { order: 0 },
  colorCollections: { order: 1 },
  colors: {
    layout: FilterConfigLayout.ColorSwatches,
    order: 2
  },
  // Size facets remain available in API data for future PDP use but are not
  // part of the category sidebar in Nuxt 2.
  imperialSizes: {
    order: 3,
    key: 'sizes',
    unitVisible: UnitVisible.Imperial,
    unitToggle: true,
    hidden: true
  },
  imperialNoteCardSizes: {
    order: 4,
    key: 'noteCardSizes',
    unitVisible: UnitVisible.Imperial,
    hidden: true
  },
  imperialEnvelopeSizes: {
    order: 5,
    key: 'envelopeCardSizes',
    unitVisible: UnitVisible.Imperial,
    hidden: true
  },
  metricSizes: {
    order: 3,
    key: 'sizes',
    unitVisible: UnitVisible.Metric,
    unitToggle: true,
    hidden: true
  },
  metricNoteCardSizes: {
    order: 4,
    key: 'noteCardSizes',
    unitVisible: UnitVisible.Metric,
    hidden: true
  },
  metricEnvelopeSizes: {
    order: 5,
    key: 'envelopeCardSizes',
    unitVisible: UnitVisible.Metric,
    hidden: true
  },
  prices: { order: 6 },
  catalogs: { order: 7 },
  stampSetLanguages: { order: 8 },
  stampMaterials: { order: 9 },
  stampSetTypes: { order: 10 },
  salesAndSpecials: { order: 11 },
  occasions: { order: 13 },
  archivalQualities: { order: 14 },
  clearBlockSizes: { order: 15, hidden: true },
  techniques: { order: 16 },
  productSuites: { order: 17 },
  seasons: { order: 18 },
  designStyles: { order: 1 },
  releaseTypes: { order: 0 },
  embellishmentTypes: { order: 12 },
  adhesiveTypes: { order: 19 },
  ribbonTypes: { order: 20 },
  specialtyPaperTypes: { order: 21 }
}

/**
 * Match param key with response key,
 * Warning: Don't refactor, just extend, used for typescript inference
 */
export const filterParam = (filter: Filter) =>
  filter === Filter.Colors
    ? 'suColors'
    : filter === Filter.Prices
      ? 'priceRanges'
      : filter

export const sortingVariants: SortingVariant[] = [
  {
    // default to newest
    sort: undefined,
    desc: undefined,
    i18nKey: 'dateDesc'
  },
  {
    sort: CatalogProductsAPIOrderBy.ItemDate,
    desc: undefined,
    i18nKey: 'dateAsc'
  },
  {
    sort: CatalogProductsAPIOrderBy.Relevance,
    desc: undefined,
    i18nKey: 'relevance'
  },
  {
    sort: CatalogProductsAPIOrderBy.ItemPrice,
    desc: undefined,
    i18nKey: 'priceAsc'
  },
  {
    sort: CatalogProductsAPIOrderBy.ItemPrice,
    desc: null,
    i18nKey: 'priceDesc'
  },
  {
    sort: CatalogProductsAPIOrderBy.ItemTitle,
    desc: undefined,
    i18nKey: 'titleAsc'
  },
  {
    sort: CatalogProductsAPIOrderBy.ItemTitle,
    desc: null,
    i18nKey: 'titleDesc'
  }
]

export const isFilterKey = (key: string): key is Filter => Boolean(filterConfig[key as Filter])

export const mapKey = (key: keyof QueryProductsAPIResponse) =>
  isFilterKey(key) ? filterParam(key) : key

type MappableQueryParamScalar = string | number | boolean | null | undefined
type MappableQueryParamValue = MappableQueryParamScalar | MappableQueryParamScalar[]
type MappableQueryParams = Record<string, MappableQueryParamValue>

export const mapQueryParams = (params: MappableQueryParams): CatalogProductsAPIQueryParams =>
  Object.fromEntries(
    Object.entries(params).map(([key, value]) => [mapKey(key as keyof QueryProductsAPIResponse), value])
  ) as CatalogProductsAPIQueryParams

export const normalizeSlug = (slug: string[] = []) => {
  const pathMatch = slug.join('/')
  return pathMatch.startsWith('/') || pathMatch === '' ? pathMatch : '/' + pathMatch;
}

export const mapValue: MapFilterValue = (filter, value) =>
  filter === Filter.Colors
    ? sortSwatches(value as ColorAPIResponse[])
    : value

export const mapValues = (filters: QueryProductsAPIResponseFilter) =>
  Object.fromEntries(Object.entries(filters).map(([key, value]) => [key, mapValue(key as Filter, value)])) as QueryProductsAPIResponseFilter

export const keys = Object.keys(filterConfig) as Filter[]

export const initialFilterValuesState = () =>
  Object.fromEntries(keys.map(key => [key, []] as [Filter, string[]])) as FilterValuesState

export const initialFilterOptionsState = () => initialFilterValuesState() as unknown as QueryProductsAPIResponseFilter

export const filterFilterOptions = (options: QueryProductsAPIResponseFilter, enabledFeatures: Set<string>) =>
  Object.fromEntries(
    Object.entries(options)
      .filter(([filter]) => isFilterKey(filter))
      .map(([filter, opts]) => {
        const cfg = filterConfig[filter as Filter]
        if (!cfg.toggle) {
          return [filter, opts]
        }
        const featureKey = cfg.toggleKey ?? `filter-${filter}`
        const featurePresent = enabledFeatures.has(featureKey)
        const visible = cfg.toggleInverse ? !featurePresent : featurePresent
        return [filter, visible ? opts : []]
      })
  ) as QueryProductsAPIResponseFilter

export const findCategory = (category: CategoryItem, slug: string): CategoryItem | null => {
  if (category.slug === slug) {
    return category
  }

  if (category.children) {
    for (const child of category.children) {
      const result = findCategory(child, slug)
      if (result) {
        return result
      }
    }
  }

  return null
}
